import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Users,
  FileText,
  ClipboardCheck,
  TrendingUp,
  ArrowRight,
  Building2,
  Download,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CURRENT_YEAR = new Date().getFullYear();
const MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function StatCard({ label, value, icon: Icon, hint, testid }) {
  return (
    <div className="bg-white border border-border rounded-md p-6 lift-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="label-eyebrow">{label}</p>
          <p className="font-display font-black text-5xl mt-3 tracking-tight text-gold-deep">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
        </div>
        <div className="w-10 h-10 border border-border rounded-md flex items-center justify-center bg-slate-50">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [month, setMonth] = useState("all");
  const [stats, setStats] = useState({ clients: 0, contracts: 0, pvs: 0, years: [] });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [pvAlerts, setPvAlerts] = useState([]);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const params = {
        year: year === "all" ? undefined : year,
        month: month === "all" ? undefined : month,
      };
      const [s, list, al, pvAl] = await Promise.all([
        api.get(`/clients/stats`, { params }),
        api.get(`/clients`, { params }),
        api.get(`/alerts/expiring-contracts`),
        api.get(`/alerts/missing-pvs`),
      ]);
      if (!alive) return;
      setStats(s.data);
      setRecent(list.data.slice(0, 6));
      setAlerts(al.data.items || []);
      setPvAlerts(pvAl.data.items || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [year, month]);

  const downloadExcel = async () => {
    setDownloadingXlsx(true);
    try {
      const { data } = await api.get("/export/xlsx", {
        params: {
          year: year === "all" ? undefined : year,
          month: month === "all" ? undefined : month,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imaggio_export${year !== "all" ? `_${year}` : ""}${month !== "all" ? `_${String(month).padStart(2, "0")}` : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Excel export failed:", e);
    }
    finally {
      setDownloadingXlsx(false);
    }
  };

  const yearOptions = useMemo(() => {
    const s = new Set([CURRENT_YEAR, ...stats.years]);
    return Array.from(s).sort((a, b) => b - a);
  }, [stats.years]);

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Panou de control</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
            Salut. Iată situația.
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="label-eyebrow">Filtru an</span>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32 h-10 bg-white" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toți anii</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth} disabled={year === "all"}>
            <SelectTrigger className="w-40 h-10 bg-white" data-testid="month-filter">
              <SelectValue placeholder="Toate lunile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate lunile</SelectItem>
              {MONTHS.map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={downloadExcel}
            disabled={downloadingXlsx}
            data-testid="dashboard-download-xlsx"
            className="h-10 px-4 border border-gold text-gold-deep bg-white rounded-md text-sm font-medium hover:bg-gold hover:text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </header>

      {/* Alerte expirare contracte */}
      {alerts.length > 0 && (
        <section
          className="bg-red-50 border-l-4 border-red-500 rounded-md p-5"
          data-testid="expiring-alerts-panel"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="label-eyebrow text-red-700">Alerte</p>
              <h2 className="font-display font-bold text-xl mt-1 text-red-900">
                Contracte care expiră ({alerts.length})
              </h2>
              <p className="text-sm text-red-800 mt-1">
                Contracte care expiră în următoarele 30 de zile sau expirate în ultimele 30 de zile.
              </p>
            </div>
            <Link
              to="/clienti"
              className="text-sm font-medium text-red-800 hover:text-red-900 underline whitespace-nowrap"
            >
              Vezi toate →
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {alerts.slice(0, 6).map((a) => (
              <Link
                key={a.contract_id}
                to={`/clienti/${a.client_id}`}
                data-testid={`alert-${a.contract_id}`}
                className="bg-white border border-red-200 rounded-md p-4 hover:border-red-400 transition-colors"
              >
                <p className="font-medium text-sm truncate">{a.client_denumire}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Contract {a.numar}</p>
                <p className={`text-xs mt-2 font-semibold ${a.zile_ramase < 0 ? "text-red-700" : "text-orange-700"}`}>
                  {a.zile_ramase < 0
                    ? `Expirat de ${-a.zile_ramase} zile`
                    : a.zile_ramase === 0
                      ? "Expiră astăzi"
                      : `Expiră în ${a.zile_ramase} zile`}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Alerte PV-uri de făcut */}
      {pvAlerts.length > 0 && (
        <section
          className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-5"
          data-testid="pv-alerts-panel"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="label-eyebrow text-amber-700">Alerte PV</p>
              <h2 className="font-display font-bold text-xl mt-1 text-amber-900">
                PV-uri de făcut ({pvAlerts.length})
              </h2>
              <p className="text-sm text-amber-800 mt-1">
                Contracte cu frecvență (1/3/12 luni) unde ultimul PV e restant sau următorul urmează în &lt;30 zile.
              </p>
            </div>
            <Link
              to="/clienti"
              className="text-sm font-medium text-amber-800 hover:text-amber-900 underline whitespace-nowrap"
            >
              Vezi toate →
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pvAlerts.slice(0, 6).map((a) => (
              <Link
                key={`pv-${a.client_id}-${a.contract_id}`}
                to={`/clienti/${a.client_id}`}
                data-testid={`pv-alert-${a.client_id}`}
                className="bg-white border border-amber-200 rounded-md p-4 hover:border-amber-400 transition-colors"
              >
                <p className="font-medium text-sm truncate">{a.client_denumire}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Contract {a.contract_numar} · La {a.frecventa_luni} {a.frecventa_luni === 1 ? "lună" : "luni"}
                </p>
                <p className={`text-xs mt-2 font-semibold ${a.zile_ramase < 0 ? "text-red-700" : "text-amber-700"}`}>
                  {a.zile_ramase < 0
                    ? `PV restant de ${-a.zile_ramase} zile`
                    : a.zile_ramase === 0
                      ? "PV azi"
                      : `PV următor în ${a.zile_ramase} zile`}
                </p>
                {a.ultima_data_pv ? (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Ultimul PV: {new Date(a.ultima_data_pv).toLocaleDateString("ro-RO")}
                  </p>
                ) : (
                  <p className="text-[10px] text-red-600 mt-1 font-semibold">Niciun PV emis până acum</p>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label={year === "all" ? "Total clienți" : `Clienți noi ${year}`}
          value={loading ? "–" : stats.clients}
          icon={Users}
          hint="Fișe deschise în perioadă"
          testid="stat-clients"
        />
        <StatCard
          label={year === "all" ? "Total contracte" : `Contracte ${year}`}
          value={loading ? "–" : stats.contracts}
          icon={FileText}
          hint="Documente emise"
          testid="stat-contracts"
        />
        <StatCard
          label={year === "all" ? "Total PV-uri" : `PV-uri ${year}`}
          value={loading ? "–" : stats.pvs}
          icon={ClipboardCheck}
          hint="Procese verbale înregistrate"
          testid="stat-pvs"
        />
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-eyebrow">Adăugați recent</p>
            <h2 className="font-display font-bold text-2xl mt-1">Ultimii clienți</h2>
          </div>
          <Link
            to="/clienti"
            className="text-sm font-medium flex items-center gap-1 text-slate-700 hover:text-gold-deep transition-colors"
            data-testid="see-all-clients"
          >
            Vezi toți <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? null : recent.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-12 text-center bg-white">
            <Building2 className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-medium">Nu ai încă niciun client</p>
            <p className="text-sm text-muted-foreground">Începe prin a adăuga primul client cu ajutorul CUI-ului.</p>
            <Link to="/clienti" className="inline-block mt-4 btn-gold hover:btn-gold px-4 py-2 rounded-md text-sm transition-colors">
              Adaugă client
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recent.map((c) => (
              <Link
                key={c.id}
                to={`/clienti/${c.id}`}
                className="bg-white border border-border rounded-md p-5 lift-hover block"
                data-testid={`client-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="label-eyebrow">CUI {c.cui}</p>
                    <p className="font-display font-bold text-lg mt-1 truncate">{c.denumire}</p>
                    <p className="text-sm text-muted-foreground mt-1 truncate">{c.localitate || c.judet || c.adresa || "—"}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {c.contracts_count} contracte</span>
                  <span className="flex items-center gap-1"><ClipboardCheck className="w-3 h-3" /> {c.pv_count} PV</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
